import { Button, Field, Input, makeStyles, mergeClasses, Textarea } from "@fluentui/react-components";
import { ArrowRight20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import IEnquiry, { NewEnquiry } from "../../models/contact/IEnquiry";
import contactService from "../../services/ContactService";
import { useState } from "react";
import Status from "../../enums/Status";
import { EnquiryFailedMessage, EnquirySubmittedMessage } from "./Messages";
import { IContactFormValidation, NewContactFormValidation, validate } from "./Validation";

declare global {
    interface Window {
        gtag_report_conversion?: (url?: string) => boolean;
    }
}

const fieldBorder = "#7C939D";
const fieldBorderHover = "#4F6671";
const accent = "#05768F";
const errorColour = "#B10E1C";

const useStyles = makeStyles({
    // The white card. If the Challenges section already wraps the form in its own
    // white card, remove that wrapper's background/padding/radius so you don't get two.
    card: {
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
        borderRadius: "24px",
        padding: "88px 40px 32px",
        boxShadow: "0 28px 70px rgba(0, 0, 0, 0.35)",
        "@media (max-width: 480px)": {
            padding: "80px 20px 24px"
        }
    },
    // Two-tone wave across the top of the card, echoing the hero's layered edge.
    wave: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "76px",
        pointerEvents: "none",
        display: "block"
    },
    formField: {
        position: "relative",
        marginBottom: "1rem",
        // Float the label when the field is focused or has content.
        // The inputs use placeholder=" " so :placeholder-shown tells us if they're empty.
        "&:focus-within > label": {
            transform: "translateY(-10px) scale(0.78)",
            color: accent
        },
        "&:has(input:not(:placeholder-shown)) > label": {
            transform: "translateY(-10px) scale(0.78)"
        },
        "&:has(textarea:not(:placeholder-shown)) > label": {
            transform: "translateY(-10px) scale(0.78)"
        },
        "@media (prefers-reduced-motion: reduce)": {
            "& > label": {
                transitionDuration: "0.01ms"
            }
        }
    },
    label: {
        position: "absolute",
        top: "17px",
        left: "17px",
        zIndex: 1,
        margin: 0,
        padding: 0,
        fontSize: "16px",
        lineHeight: "1.2",
        color: "#5B6B73",
        pointerEvents: "none",
        transformOrigin: "left top",
        transitionProperty: "transform, color",
        transitionDuration: "0.15s",
        transitionTimingFunction: "ease"
    },
    // Fluent's Input/Textarea root is a <span> that draws the border; style it here.
    inputRoot: {
        width: "100%",
        minHeight: "56px",
        borderRadius: "12px",
        backgroundColor: "#F4F8F9",
        border: `1px solid ${fieldBorder}`,
        transitionProperty: "border-color, box-shadow, background-color",
        transitionDuration: "0.15s",
        "&:hover": {
            border: `1px solid ${fieldBorderHover}`
        },
        "&:focus-within": {
            backgroundColor: "#FFFFFF",
            border: `1px solid ${accent}`,
            boxShadow: "0 0 0 4px rgba(5, 118, 143, 0.2)"
        },
        // Hide Fluent's built-in bottom focus underline; we use the ring above.
        "::after": {
            display: "none"
        }
    },
    inputRootError: {
        border: `1px solid ${errorColour}`,
        "&:hover": {
            border: `1px solid ${errorColour}`
        },
        "&:focus-within": {
            border: `1px solid ${errorColour}`,
            boxShadow: "0 0 0 4px rgba(177, 14, 28, 0.15)"
        }
    },
    inputInner: {
        paddingTop: "22px",
        paddingBottom: "6px",
        paddingLeft: "16px",
        paddingRight: "16px",
        fontSize: "16px"
    },
    textareaInner: {
        paddingTop: "28px",
        paddingBottom: "8px",
        paddingLeft: "16px",
        paddingRight: "16px",
        fontSize: "16px",
        minHeight: "128px"
    },
    contactButton: {
        width: "100%",
        height: "54px",
        borderRadius: "12px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        fontWeight: 700,
        marginTop: "0.25rem"
    },
    privacy: {
        margin: "0.75rem 0 0",
        textAlign: "center",
        fontSize: "13px",
        color: "#5B6B73"
    }
});

export default function Form() {
    const { t } = useTranslation("contact");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.NEURAL);
    const [enquiry, setEnquiry] = useState<IEnquiry>(NewEnquiry());
    const [validation, setValidation] = useState<IContactFormValidation>(NewContactFormValidation());

    const handleFieldChange = (field: keyof IEnquiry, value: string) => {
        setEnquiry(e => ({ ...e, [field]: value }));
        setValidation(v => ({ ...v, [field]: false }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (validate(enquiry, setValidation)) {
            const success = await submitEnquiry(enquiry, setStatus);
            if (success) {
                window.gtag_report_conversion?.();
            }
        }
    };

    const isDisabled = [Status.SAVING, Status.SAVE_SUCCESS].includes(status);

    const emailHasError = validation.email || validation.emailInvalid;
    const emailValidationMessage = validation.email
        ? t("form.fields.email.required")
        : validation.emailInvalid
            ? t("form.fields.email.invalid")
            : null;

    // Short floating labels. Add form.fields.<name>.label to the translations to override the English defaults.
    const labelFor = (text: string) => ({ className: styles.label, children: text });

    return (
        <div className={styles.card}>
            <svg
                className={styles.wave}
                viewBox="0 0 566 76"
                preserveAspectRatio="none"
                aria-hidden="true"
                focusable="false"
            >
                <path d="M0 0H566V50C490 72 410 70 340 54C270 38 200 34 130 48C80 58 35 60 0 52Z" fill="#E6F3F7" />
                <path d="M0 0H566V30C505 50 445 50 385 40C315 28 245 22 175 34C115 44 55 48 0 36Z" fill="#CFE6EC" />
            </svg>

            <form onSubmit={handleSubmit} noValidate>
                <Field
                    className={styles.formField}
                    label={labelFor(t("form.fields.name.label", "Your name"))}
                    validationState={validation.name ? "error" : "none"}
                    validationMessage={validation.name ? t("form.fields.name.required") : null}
                >
                    <Input
                        type="text"
                        size="large"
                        placeholder=" "
                        value={enquiry.name}
                        disabled={isDisabled}
                        className={mergeErr(styles.inputRoot, styles.inputRootError, validation.name)}
                        input={{ className: styles.inputInner }}
                        onChange={(_, data) => handleFieldChange("name", data.value)}
                    />
                </Field>

                <Field
                    className={styles.formField}
                    label={labelFor(t("form.fields.phone.label", "Phone number"))}
                    validationState={validation.phone ? "error" : "none"}
                    validationMessage={validation.phone ? t("form.fields.phone.required") : null}
                >
                    <Input
                        type="tel"
                        size="large"
                        placeholder=" "
                        value={enquiry.phone}
                        disabled={isDisabled}
                        className={mergeErr(styles.inputRoot, styles.inputRootError, validation.phone)}
                        input={{ className: styles.inputInner }}
                        onChange={(_, data) => handleFieldChange("phone", data.value)}
                    />
                </Field>

                <Field
                    className={styles.formField}
                    label={labelFor(t("form.fields.email.label", "Email address"))}
                    validationState={emailHasError ? "error" : "none"}
                    validationMessage={emailValidationMessage}
                >
                    <Input
                        type="email"
                        size="large"
                        placeholder=" "
                        value={enquiry.email}
                        disabled={isDisabled}
                        className={mergeErr(styles.inputRoot, styles.inputRootError, emailHasError)}
                        input={{ className: styles.inputInner }}
                        onChange={(_, data) => handleFieldChange("email", data.value)}
                    />
                </Field>

                <Field
                    className={styles.formField}
                    label={labelFor(t("form.fields.message.label", "Your message"))}
                    validationState={validation.message ? "error" : "none"}
                    validationMessage={validation.message ? t("form.fields.message.required") : null}
                >
                    <Textarea
                        rows={4}
                        size="large"
                        placeholder=" "
                        disabled={isDisabled}
                        value={enquiry.message}
                        className={mergeErr(styles.inputRoot, styles.inputRootError, validation.message)}
                        textarea={{ className: styles.textareaInner }}
                        onChange={(_, data) => handleFieldChange("message", data.value)}
                    />
                </Field>

                {
                    status === Status.SAVE_ERROR && <EnquiryFailedMessage />
                }
                {
                    status === Status.SAVE_SUCCESS && <EnquirySubmittedMessage />
                }

                {
                    [Status.NEURAL, Status.SAVING, Status.SAVE_ERROR].includes(status) &&
                    <>
                        <Button
                            type="submit"
                            appearance="primary"
                            size="large"
                            disabled={isDisabled}
                            className={styles.contactButton}
                            icon={<ArrowRight20Regular />}
                            iconPosition="after"
                        >
                            {t("form.button")}
                        </Button>
                        <p className={styles.privacy}>
                            {t("form.privacy", "We'll only use your details to get back to you.")}
                        </p>
                    </>
                }
            </form>
        </div>
    );
}

// Join the base class with the error class when the field is invalid (mergeClasses keeps Griffel overrides correct).
function mergeErr(base: string, error: string, isError: boolean): string {
    return mergeClasses(base, isError && error);
}

async function submitEnquiry(
    enquiry: IEnquiry,
    setStatus: (status: Status) => void
): Promise<boolean> {
    setStatus(Status.SAVING);

    try {
        const result = await contactService.submitEnquiry(enquiry);
        setStatus(result ? Status.SAVE_SUCCESS : Status.SAVE_ERROR);
        return !!result;
    } catch {
        setStatus(Status.SAVE_ERROR);
        return false;
    }
}